import { useEffect } from "react";
import { useSearchParams } from "react-router";

const DEFAULT_SCREEN_VIEW = "table";

/**
 * Opt-in two-way binding between a table screen's active view and a URL search
 * parameter, so a view can be linked to and redirected into. Screens that pass
 * no `param` keep the existing behaviour, where the view lives only in
 * workspace state.
 *
 * Returns the writer to call when the user picks a view.
 */
export function useScreenViewUrlSync({
  param,
  activeScreenView,
  validScreenViews,
  setScreenView,
}: {
  param?: string;
  activeScreenView: string;
  validScreenViews: string[];
  setScreenView: (screen: string) => void;
}): (screen: string) => void {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = param ? searchParams.get(param) : null;
  // Extra tabs arrive with the feature flags, so a view named in the URL can
  // become valid on a later render.
  const isRequestable = !!requested && validScreenViews.includes(requested);

  useEffect(() => {
    if (isRequestable && requested !== activeScreenView) {
      setScreenView(requested as string);
    }
    // Reacts to the URL asking for a different view, not to the view changing:
    // adding activeScreenView here would undo the user's own switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps, @eslint-react/exhaustive-deps
  }, [isRequestable, requested]);

  return (screen: string) => {
    if (!param) return;
    const next = new URLSearchParams(searchParams);
    if (screen === DEFAULT_SCREEN_VIEW) next.delete(param);
    else next.set(param, screen);
    setSearchParams(next, { replace: true });
  };
}
